import { cloneDeep, isEmpty, isNil, keyBy } from 'lodash-es';
import { acceptHMRUpdate, defineStore } from 'pinia';
import { useRoute } from 'vue-router';
import { useAuthStore } from '~/auth/stores/auth.store';
import { useBiDataFormatter } from '~/bi/components/widgets/table-widget/composables/bi-table-data-formatter.composable';
import { useBIQueryBuilder } from '~/bi/composables/useBIQueryBuilder.js';

const bi_query_builder = useBIQueryBuilder();

export function useBiStore(key) {
  const auth_store = useAuthStore();
  const route = useRoute();
  const connection_id = key || `bi_${auth_store?.current_organization?.uid}${route.params.asset_id ? `__${route.params.asset_id}` : ''}`;
  return defineStore(key || connection_id, {
    state: () => ({
      scope: 'global',
      dashboards: {},
      selected_dashboard: null,
      dashboard_applied_global_filters_map: {},
      all_schemas: [],
      all_schema_by_name: {},
      all_schema_columns: {},
      selected_stage_index: null,
      is_chart_builder_form_valid: false,
      table_widget_config_change_detector: false,
      query_config_error: null,
      widget_builder_data: [],
      widget_builder_metadata: {},
      widget_builder_config_change_detector: 1,
      sql_query: '',
      fetching_widget_data: false,
      pre_defined_filter_name: 'New Filter',
      widget_builder_config: {
        query: {
          selected_database: null,
          stages: [],
        },
        chart: {},
        quick_filters: [],
      },
      chart_builder_columns_data_types: {},
      modified_widgets: {
        created: {},
        updated: {},
      },
      current_grid_layout: [],
      is_fullscreen: false,
      selected_global_filter_uid: null, // currently selected global filter uid in the BI Global Filter Config popup
      distinct_column_values_response_cache: {}, // Cache for distinct column values for global filters
      rendered_widgets: {},
      echarts_resize_observers: {},
    }),
    getters: {
      sorted_dashboards() {
        return Object.values(this.dashboards || {}).sort((a, b) => a?.name?.localeCompare?.(b?.name));
      },
      selected_dashboard_details() {
        return this.dashboards[this.selected_dashboard];
      },
      get_global_filters() {
        return this.dashboards[this.selected_dashboard]?.meta?.global_filters || [];
      },
      get_global_filters_with_applied_filters() {
        // Contains only those global filters which have applied_filter_config in the map
        return (this.dashboards[this.selected_dashboard]?.meta?.global_filters || []).map(filter => ({
          ...filter,
          applied_filter_config: this.get_applied_filters_for_dashboard[filter.uid],
        })).filter(filter => filter.applied_filter_config);
      },
      get_applied_filters_for_dashboard() {
        return this.dashboard_applied_global_filters_map[this.selected_dashboard] || {};
      },
      connection_id() {
        return auth_store?.current_organization?.uid + (route.params.asset_id ? `__${route.params.asset_id}` : '');
      },
      are_all_widgets_rendered() {
        const total_widgets = this.current_grid_layout?.length || 0;
        if (total_widgets === 0)
          return true;
        const rendered_widgets_length = Object.keys(this.rendered_widgets).length;
        return rendered_widgets_length >= total_widgets;
      },
    },
    actions: {
      set_scope(scope) {
        this.scope = scope;
      },
      set_is_fullscreen(value) {
        this.is_fullscreen = value;
      },
      markWidgetRendered(widget_id) {
        this.rendered_widgets[widget_id] = true;
      },
      resetRenderedWidgets() {
        this.rendered_widgets = {};
      },
      async initializeStore() {
        try {
          const query = {};
          if (this.scope === 'micro_app')
            query.asset = route.params.asset_id;
          const { data } = await this.$services.bi_dashboard.getAll({ query });
          data.report = data.report.map((dashboard) => {
            dashboard.widgets = dashboard.report_data;
            delete dashboard.report_data;
            return dashboard;
          });
          this.dashboards = keyBy(data.report, 'uid');
        }
        catch (err) {
          logger.log('Failed to initialize BI dashboards:', err);
          this.dashboards = {};
        }
      },
      async fetchDashboard(query = {}) {
        try {
          const { data } = await this.$services.bi_dashboard.get_dashboard({
            id: this.selected_dashboard,
            ...(Object.keys(query).length && { query }),
          });
          if (data?.report?.length) {
            const modified_report = data.report[0];
            modified_report.widgets = modified_report.report_data;
            delete modified_report.report_data;
            this.dashboards[modified_report.uid] = modified_report;
          }
        }
        catch (err) {
          logger.log('Failed to fetch BI dashboard:', err);
        }
      },

      async fetchPublicDashboard(token, query = {}) {
        try {
          const { data } = await this.$services.bi_dashboard.get_public_dashboard({
            token,
            ...(Object.keys(query).length && { query }),
          });
          if (data?.report?.length) {
            const modified_report = data.report[0];
            modified_report.widgets = modified_report.report_data;
            delete modified_report.report_data;
            this.dashboards[modified_report.uid] = modified_report;
          }
        }
        catch (err) {
          logger.log('Failed to fetch public BI dashboard:', err);
          throw err;
        }
      },

      async createDashboard(dashboard_data, select = true) {
        try {
          const current_route = this.$router.currentRoute.value;
          const payload = {
            asset: current_route.params.asset_id,
            name: dashboard_data.name,
            report_data: dashboard_data.widgets || [],
            ...dashboard_data,
          };
          const { data } = await this.$services.bi_dashboard.post({
            body: payload,
          });

          if (data?.report?.length) {
            const modified_report = data.report[0];
            modified_report.widgets = modified_report.report_data;
            delete modified_report.report_data;
            this.dashboards[data.report[0].uid] = modified_report;
            if (select)
              this.selected_dashboard = data.report[0].uid;
          }
          return data?.report || [];
        }
        catch (err) {
          logger.error('Failed to create BI dashboard:', err);
          throw err;
        }
      },
      async updateDashboard(uid, updates) {
        try {
          if (this.dashboards[uid]) {
            const updated_dashboard = { ...this.dashboards[uid], ...updates };

            const { data } = await this.$services.bi_dashboard.patch_dashboard({
              id: uid,
              body: updated_dashboard,
            });

            if (data?.report?.length) {
              const modified_report = data.report[0];
              modified_report.widgets = modified_report.report_data;
              delete modified_report.report_data;
              this.dashboards[uid] = modified_report;
            }
            else {
              this.dashboards[uid] = updated_dashboard;
            }
          }
        }
        catch (err) {
          logger.error('Failed to update BI dashboard:', err);
          throw err;
        }
      },
      async deleteDashboard(uid) {
        const all_dashboards = { ...this.dashboards };
        try {
          if (this.dashboards[uid]) {
            await this.$services.bi_dashboard.delete({
              body: {
                reports: [
                  {
                    uid,
                  },
                ],
              },
            });
            delete this.dashboards[uid];

            if (this.selected_dashboard === uid) {
              const remaining_dashboards = Object.keys(this.dashboards);
              this.selected_dashboard = remaining_dashboards.length > 0 ? remaining_dashboards[0] : null;
            }
          }
        }
        catch (err) {
          logger.error('Failed to delete BI dashboard:', err);
          // Restore the dashboard on error
          this.dashboards = all_dashboards;
          throw err;
        }
      },

      createWidget(uid, widget_builder_data, layout_config = null) {
        this.modified_widgets.created[uid] = widget_builder_data;

        let current_layout = this.current_grid_layout;
        if (layout_config?.y) {
          current_layout = current_layout.map((widget) => {
            if (widget.y >= layout_config.y) {
              return { ...widget, y: widget.y + widget.h };
            }
            return widget;
          });
        }
        const x = 0;
        const y = current_layout.length > 0
          ? Math.max(...current_layout.map(w => w.y + w.h))
          : 0;
        const existing_ids = current_layout.map((w) => {
          const parsed = Number.parseInt(w.i);
          return Number.isNaN(parsed) ? 0 : parsed;
        });
        const unique_i = String(existing_ids.length ? Math.max(...existing_ids) + 1 : 1);
        const updated_layout = [...current_layout, {
          x: layout_config?.x || x,
          y: layout_config?.y || y,
          w: layout_config?.w || 5,
          h: layout_config?.h || 2,
          i: unique_i,
          uid,
          name: widget_builder_data.name,
          config: widget_builder_data.config,
          moved: false,
        }];
        this.current_grid_layout = updated_layout;
      },
      updateWidget(uid, updates) {
        if (this.modified_widgets.created[uid]) {
          this.modified_widgets.created[uid] = { ...this.modified_widgets.created[uid], ...updates };
        }
        else {
          this.modified_widgets.updated[uid] = updates;
        }

        const layoutIndex = this.current_grid_layout.findIndex(widget => widget.uid === uid);
        if (layoutIndex !== -1) {
          this.current_grid_layout[layoutIndex] = { ...this.current_grid_layout[layoutIndex], ...updates };
        }
      },
      deleteWidget(uid) {
        this.current_grid_layout = this.current_grid_layout.filter(widget => widget.uid !== uid);
        delete this.modified_widgets.created[uid];
        delete this.modified_widgets.updated[uid];
      },

      set_chart_builder_columns_data_types() {
        const alias_to_column_mapping = this.alias_to_column_mapping();
        const getFieldType = (field_type) => {
          switch (field_type) {
            case 'string':
            case 'text':
            case 'text_array':
              return 'text';

            case 'numeric':
            case 'integer':
            case 'decimal':
            case 'float':
              return 'numeric';

            case 'date':
            case 'time':
            case 'timestamp':
              return 'date';

            case 'boolean':
              return 'boolean';
          }
        };
        this.chart_builder_columns_data_types = Object.keys(alias_to_column_mapping).reduce((acc, key) => {
          acc[key] = getFieldType(alias_to_column_mapping[key].field_type);
          return acc;
        }, {});
        this.widget_builder_metadata.columns?.forEach((column) => {
          if (!this.chart_builder_columns_data_types[column.name]) {
            this.chart_builder_columns_data_types[column.name] = getFieldType(column.type);
          }
        });
      },
      alias_to_column_mapping(stage_config = null) {
        const stages = stage_config || this.widget_builder_config.query.stages;
        if (!stages.length && !stages?.[0]?.columns?.length)
          return {};
        const column_mapping = {};
        const selected_stage_index = this.selected_stage_index;
        const filtered_stages = stages.filter((stage, index) => isNil(selected_stage_index) ? true : selected_stage_index >= index);
        filtered_stages.forEach((stage, stage_index) => stage?.columns?.forEach((column) => {
          column_mapping[column.alias] = { ...column, is_last_stage: (filtered_stages.length - 1) === stage_index };
        }));
        return column_mapping;
      },
      async reset_widget_builder() {
        this.widget_builder_config = {
          query: {
            selected_database: this.connection_id,
            stages: [],
          },
          chart: {},
        };
        this.selected_stage_index = null;
        this.widget_builder_data = [];
        this.query_config_error = null;
      },
      async getSchemas() {
        const response = await this.$services.bi_schema.table({
          id: this.connection_id,
        });
        this.all_schemas = response.data.tables.map(table => ({ label: table.label || table.name, name: table.name, path: table.path, columns: table.columns.map(column => ({ ...column, table_name: table.name, field: bi_query_builder.constructFieldName({ label: column.name, table_name: table.name }), label: column.label || column.name, alias: bi_query_builder.constructColumnAlias({ table_name: table.label, path: column.path, column_name: column.label || column.name }) })) }));
        this.all_schema_by_name = keyBy(this.all_schemas, 'name');
        return response.data;
      },
      async getTableColumns({ table_name, prefix_alias }) {
        if (!prefix_alias) {
          Object.entries(this.all_schema_columns).forEach(([schema_name, columns]) => columns.forEach((column) => {
            const table_label = this.all_schema_by_name[schema_name]?.label;
            column.alias = bi_query_builder.constructColumnAlias({ column_name: column.label || column.name, path: column.path, table_name: schema_name === table_name ? '' : table_label });
          }));
        }
        if (this.all_schema_columns[table_name]) {
          return this.all_schema_columns[table_name];
        }
        const response = await this.$services.bi_schema.columns({
          id: this.connection_id,
          table_name,
        });
        let { columns } = response.data;
        const table_label = this.all_schema_by_name[table_name]?.label;
        columns = columns.map(column => ({ ...column, table_name, label: column.label || column.name, is_table_field: true, field: bi_query_builder.constructFieldName({ label: column.name, table_name }), alias: bi_query_builder.constructColumnAlias({ column_name: column.label || column.name, path: column.path, table_name: prefix_alias ? table_label : '' }) }));
        this.all_schema_columns[table_name] = columns;
      },
      async selectTable(table) {
        await this.getTableColumns({ table_name: table.name });
      },
      applyQuickFiltersToStages(stages, quick_filters) {
        const stages_with_filters = cloneDeep(stages);
        quick_filters.forEach((quick_filter) => {
          if (!quick_filter.applied_value) {
            return;
          }

          const is_date_field = ['date', 'timestamp', 'datetime', 'timestamptz'].includes(quick_filter.type);
          const is_array_field = quick_filter.type === 'array';

          if (quick_filter.date_filter_mode === 'date_interval') {
            stages_with_filters.forEach((stage) => {
              stage.columns.forEach((column) => {
                // Match by alias for date_interval filters (as per applyGlobalFilters from BiGrid.vue)
                if (column.alias === quick_filter.alias) {
                  column.dateBin = quick_filter.applied_value;
                  column.is_bin = true;
                }
              });
            });
            return;
          }

          let operator;
          if (is_date_field)
            operator = 'date_is';
          else if (is_array_field)
            operator = 'contains_any_of';
          else
            operator = 'equals';
          const filter_value = is_array_field ? [quick_filter.applied_value] : quick_filter.applied_value;

          const filter_payload = {
            field: quick_filter.field,
            op: operator,
            value: filter_value,
          };

          const target_stage_index = quick_filter.stage_index !== undefined
            ? quick_filter.stage_index
            : 0;

          if (target_stage_index >= 0 && target_stage_index < stages_with_filters.length) {
            const existing_filter_index = stages_with_filters[target_stage_index].filters.findIndex(
              f => f.field === quick_filter.field,
            );
            if (existing_filter_index >= 0) {
              stages_with_filters[target_stage_index].filters[existing_filter_index] = filter_payload;
            }
            else {
              stages_with_filters[target_stage_index].filters.push(filter_payload);
            }
          }
        });
        return stages_with_filters;
      },
      async getWidgetData(stages, uid = 'preview', signal, quick_filters = []) {
        try {
          this.fetching_widget_data = uid;

          let stages_with_quick_filters = stages;
          if (quick_filters && quick_filters.length) {
            stages_with_quick_filters = this.applyQuickFiltersToStages(stages, quick_filters);
          }

          let stage_config = bi_query_builder.getStageConfig(stages_with_quick_filters);
          if (this.selected_stage_index !== null) {
            stage_config = stage_config.slice(0, this.selected_stage_index + 1);
          }
          const columns_mapping = this.alias_to_column_mapping(stage_config);
          const response = await this.$services.bi_query.execute({
            id: this.connection_id,
            body: stage_config,
            signal,
          });
          this.sql_query = response.data.sql;
          this.query_config_error = null;
          const { data, metadata } = response.data;
          const formatted_data = useBiDataFormatter().formatWidgetData(data, metadata, columns_mapping);
          logger.log(formatted_data, metadata);
          this.fetching_widget_data = false;
          return { data: formatted_data, metadata, sql_query: response.data.sql };
        }
        catch (error) {
          if (error.code !== 'ERR_CANCELED') {
            this.query_config_error = error.data;
            this.sql_query = error.data?.sql || '';
            this.fetching_widget_data = false;
            return { data: [], metadata: null };
          }
        }
      },
      async getColumnValues({ table_name, column_name, query }) {
        const response = await this.$services.bi_schema.column_values({
          id: this.connection_id,
          table_name,
          column_name,
          query,
        });
        return response.data;
      },
      resetSelectedGlobalFilter() {
        this.selected_global_filter_uid = null;
      },
      updateGlobalFilters(global_filters) {
        const active_dashboard = this.dashboards[this.selected_dashboard];
        if (isEmpty(active_dashboard.meta)) {
          active_dashboard.meta = {
            global_filters,
          };
        }
        else {
          active_dashboard.meta = {
            ...(active_dashboard.meta || {}),
            global_filters,
          };
        }
      },
      updateAppliedGlobalFilter(uid, applied_filter_config) {
        if (this.dashboard_applied_global_filters_map[this.selected_dashboard]) {
          this.dashboard_applied_global_filters_map[this.selected_dashboard][uid] = applied_filter_config;
        }
        else {
          this.dashboard_applied_global_filters_map[this.selected_dashboard] = { [uid]: applied_filter_config };
        }
      },
      async createShareLink(dashboard_id, organization_id) {
        try {
          const { data } = await this.$services.bi_dashboard.create_share_link({
            id: dashboard_id,
            query: { organization: organization_id },
            body: { organization: organization_id },
          });
          if (this.dashboards[dashboard_id])
            this.dashboards[dashboard_id].shared_link = data;
          return data;
        }
        catch (err) {
          logger.error('Failed to create share link:', err);
          throw err;
        }
      },
      async revokeShareLink(dashboard_id, organization_id) {
        try {
          await this.$services.bi_dashboard.revoke_share_link({
            id: dashboard_id,
            query: { organization: organization_id },
          });
          if (this.dashboards[dashboard_id])
            this.dashboards[dashboard_id].shared_link = null;
        }
        catch (err) {
          logger.error('Failed to revoke share link:', err);
          throw err;
        }
      },
      clearAllAppliedGlobalFilters() {
        this.dashboard_applied_global_filters_map[this.selected_dashboard] = {};
      },
      removeAppliedGlobalFilter(uid) {
        delete this.dashboard_applied_global_filters_map[this.selected_dashboard]?.[uid];
      },
      applyDefaultFilters() {
        // Apply default filters as pre-applied in the dashboard
        this.get_global_filters.forEach((filter) => {
          if (filter.general.set_default_filter_value && !isEmpty(filter.general.default_filter_config)) {
            const create_apply_filter_payload = {
              op: filter.general.default_filter_config.op,
              value: filter.general.default_filter_config.operator_value,
              meta: {
                is_stage_field: false,
                filter_config: {
                  operator_value: filter.general.default_filter_config.operator_value,
                  ...filter.general.default_filter_config,
                },
              },
            };

            this.updateAppliedGlobalFilter(filter.uid, create_apply_filter_payload);
          }
        });
      },
    },
  })();
}

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useBiStore, import.meta.hot));
